import { UnfoldLess, UnfoldMore } from '@mui/icons-material';
import { Box, Grid2 as Grid, Theme } from '@mui/material';
import { useDispatch } from 'react-redux';

import { Button } from '@/components';
import { AnalyticsService, EVENT_CONSTANTS, EVENTS } from '@/services';
import {
  playerActions,
  selectActiveChapterId,
  selectActiveEpisodeId,
  selectEpisodeCompliance,
  selectIsExpandedPlayerGlowDismissed,
  selectIsPlayerExpanded,
  useAppSelector,
  useFetchChapterQuery,
  useFetchEpisodeQuery,
  userPreferencesActions,
} from '@/store';

import { ProgressBar } from '../ProgressBar';
import { VolumeSlider } from '../VolumeSlider';
import { ChapterInfo, MediaControls, ProgressInfo } from './components';

export const MiniPlayer = () => {
  const dispatch = useDispatch();
  const activeChapterId = useAppSelector(selectActiveChapterId);
  const activeEpisodeId = useAppSelector(selectActiveEpisodeId);
  const isExpanded = useAppSelector(selectIsPlayerExpanded);
  const compliance = useAppSelector(state =>
    selectEpisodeCompliance(state, activeEpisodeId),
  );
  const { isVideo, isTrailer } = useFetchChapterQuery(
    {
      chapterId: activeChapterId!,
      compliance,
    },
    {
      selectFromResult: result => ({
        isVideo: result.data?.type === 'video',
        isTrailer: result.data?.isTrailer,
      }),
    },
  );
  const { data: episode } = useFetchEpisodeQuery({
    episodeId: activeEpisodeId!,
    compliance,
  });

  const location = { component: EVENT_CONSTANTS.COMPONENT.TRACK_PLAYER };

  const isExpandedPlayerGlowDismissed = useAppSelector(
    selectIsExpandedPlayerGlowDismissed,
  );

  const onToggleExpanded = () => {
    if (!isVideo) {
      if (isExpanded) {
        AnalyticsService.logEvent(
          EVENTS.VISUAL_LEARNING_EVENTS.CLOSE_VISUAL_LEARNING,
          {
            episodeId: activeEpisodeId!,
            actor: 'user',
          },
        );
      } else {
        dispatch(userPreferencesActions.dismissExpandedPlayerGlow());
        AnalyticsService.logEvent(
          EVENTS.VISUAL_LEARNING_EVENTS.OPEN_VISUAL_LEARNING,
          {
            episodeId: activeEpisodeId!,
            actor: 'user',
          },
        );
      }
    }
    dispatch(playerActions.setExpanded(!isExpanded));
  };

  return (
    <Box sx={styles.miniPlayerContainer}>
      <Box sx={styles.progressBarContainer}>
        <ProgressBar referrer={location} />
      </Box>
      <Grid container alignItems="center">
        <Grid size={{ xs: 3, sm: 4 }}>
          <ChapterInfo referrer={location} />
        </Grid>
        <Grid size={{ xs: 6, sm: 5, lg: 4 }}>
          <MediaControls />
        </Grid>
        <Grid size={{ xs: 3, sm: 3, lg: 4 }}>
          <Box sx={styles.playerRight}>
            <ProgressInfo />
            <Box sx={styles.volumeControls}>
              <VolumeSlider referrer={location} />
            </Box>
            {(isVideo ||
              (!isTrailer && !!episode?.visualAid?.horizontalPDF)) && (
              <Button
                variant="tertiary"
                startIcon={isExpanded ? <UnfoldLess /> : <UnfoldMore />}
                skinny={false}
                fullWidth={false}
                onClick={onToggleExpanded}
                colorOverride="white"
                sx={[
                  !isExpandedPlayerGlowDismissed && styles.expandToggleGlow,
                  { mr: { sm: 1 } },
                ]}
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
                aria-expanded={isExpanded}
              />
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const styles = {
  miniPlayerContainer: (theme: Theme) => ({
    position: 'relative',
    backgroundColor: 'primary.main',
    color: 'text.contrast',
    height: theme.dimensions.miniPlayer.height,
    width: '100%',
    pointerEvents: 'auto',
  }),
  progressBarContainer: (theme: Theme) => ({
    position: 'absolute',
    top: 0,
    left: 90,
    width: 'calc(100% - 90px)',
    [theme.breakpoints.down('sm')]: {
      left: 66,
      width: 'calc(100% - 66px)',
      transform: 'translateY(calc(-50% + 2px))',
    },
  }),
  playerRight: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    justifyContent: 'flex-end',
    pt: 0.5,
  },
  volumeControls: {
    mr: 2.5,
    display: { xs: 'none', lg: 'block' },
  },
  expandToggleGlow: {
    boxShadow:
      '0 0 16px rgba(84, 94, 212, 0.6), 0 0 30px rgba(84, 94, 212, 0.35)',
    animation: 'miniPlayerGlowPulse 1.2s ease-in-out infinite',
    '@keyframes miniPlayerGlowPulse': {
      '0%': {
        boxShadow:
          '0 0 12px rgba(84, 94, 212, 0.5), 0 0 24px rgba(84, 94, 212, 0.3)',
      },
      '50%': {
        boxShadow:
          '0 0 18px rgba(84, 94, 212, 0.75), 0 0 34px rgba(84, 94, 212, 0.95), 0 0 52px rgba(84, 94, 212, 0.55)',
      },
      '100%': {
        boxShadow:
          '0 0 12px rgba(84, 94, 212, 0.5), 0 0 24px rgba(84, 94, 212, 0.3)',
      },
    },
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
};
